import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

/**
 * Shared native-platform handling for "open some other site, then come back
 * signed in / subscribed / whatever changed" flows -- used by both
 * AuthService (Cognito/Google OAuth) and SubscriptionService (Stripe
 * checkout). Both hit the exact same underlying problem on native: Google
 * blocks OAuth inside an embedded WebView outright (confirmed via a real
 * 400 "malformed request" error at Google's consent step), and Stripe's
 * checkout domain was never in capacitor.config.json's allowNavigation
 * either -- so by Capacitor's own default, navigating the app's WebView
 * directly to either one bounces out to the external system browser, whose
 * storage is completely separate from the native WebView's. The user
 * completes sign-in/payment fine, but the app itself never sees it.
 *
 * Fix: open these URLs in a Chrome Custom Tab instead (openExternalUrl) --
 * not an embedded WebView, so Google allows it -- and catch the return trip
 * via the verified Android App Link for zephyr.travel (see
 * AndroidManifest.xml and https://zephyr.travel/.well-known/
 * assetlinks.json), which Android hands to this app as an appUrlOpen event
 * instead of actually loading in a browser.
 */

let listenerRegistered = false;

/** Opens a URL in a Custom Tab on native, a normal same-tab redirect on web. */
export async function openExternalUrl(url) {
    if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
    } else {
        window.location.href = url;
    }
}

/**
 * Registers one shared appUrlOpen listener for the whole app. Whatever
 * query string came back on the App Link (?code=&state= from Cognito,
 * ?subscribed=true from Stripe, or anything else opened via
 * openExternalUrl() in the future) gets forwarded onto a reload of the
 * app's own current (pinned beta/vN) page -- same shape a plain browser
 * redirect produces -- so every existing page-load-time check
 * (handleCallback()'s ?code= check, the ?subscribed=true handler in
 * main.js) runs completely unchanged, no logic duplicated here.
 *
 * Safe to call more than once (e.g. from both AuthService's and
 * SubscriptionService's constructors) -- only the first call registers it.
 */
export function initNativeUrlOpenListener() {
    if (!Capacitor.isNativePlatform() || listenerRegistered) return;
    listenerRegistered = true;

    App.addListener('appUrlOpen', async (event) => {
        let incoming;
        try {
            incoming = new URL(event.url);
        } catch {
            return;
        }
        // No query string: the return from a Cognito logout (AuthService
        // signOut) -- the app already reloaded itself signed out, so just
        // close the Custom Tab.
        if (!incoming.search) {
            await Browser.close().catch(() => {});
            return;
        }

        await Browser.close().catch(() => {});
        window.location.href = `${window.location.pathname}${incoming.search}`;
    });
}
